import React from "react";
import ReactDOM from "react-dom";
import ReactExport from "react-export-excel";
import { languages } from "../../constants/languages";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export const exportReferees = async (tournament, teams) => {
  try {
    // Get all referees
    const refereesSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("referees")
      .get();

    const referees = [];
    refereesSnapshot.forEach((doc) => {
      let referee = doc.data();
      referee.id = doc.id;
      referees.push(referee);
    });

    // Get login codes for each referee
    const loginCodes = await Promise.all(
      referees.map(async (referee) => {
        const codeSnapshot = await window.firebase
          .firestore()
          .collection("tournaments")
          .doc(tournament[".key"])
          .collection("teamCodes")
          .where("teamId", "==", referee.id)
          .where("type", "==", "referee")
          .get();

        let code = "";
        codeSnapshot.forEach((doc) => {
          code = doc.id;
        });

        let url =
          "https://" +
          (tournament.isPSD || tournament.crqpsd
            ? "tournify.prosoccerdata.com/"
            : "tournifyapp.com/live/") +
          tournament.liveLink +
          "/referee/" +
          referee.id +
          "/" +
          code;

        return {
          refereeId: referee.id,
          loginURL: code && tournament.liveLink ? url : "",
        };
      })
    );

    // Prepare columns
    let columns = [{ id: "name", label: languages.teams.name }];

    // Add country column if enabled
    if (((tournament.defaultFields || {}).refereeCountry || {}).show) {
      columns.push({ id: "refereeCountry", label: languages.teams.country });
    }

    // Add login URL column if enabled
    if (((tournament.defaultFields || {}).refereeShareURL || {}).show  ||
        ((tournament.defaultFields || {})["refereeShareURL"] || {}).show === undefined) {
      columns.push({
        id: "refereeShareURL",
        label: languages.teamInfo.shareURL,
      });
    }

    // Add custom info fields
    Object.keys(tournament.infoFieldsReferees || {}).forEach((key) => {
      columns.push({
        id: key,
        label: tournament.infoFieldsReferees[key].name,
      });
    });

    // Sort referees
    const sortedReferees = referees.sort((a, b) => {
      function chunkify(t) {
        var tz = [],
          x = 0,
          y = -1,
          n = 0,
          i,
          j;

        while ((i = (j = t.charAt(x++)).charCodeAt(0))) {
          var m = i === 46 || (i >= 48 && i <= 57);
          if (m !== n) {
            tz[++y] = "";
            n = m;
          }
          tz[y] += j;
        }
        return tz;
      }

      let toChunkA = a["name"] || "";
      let toChunkB = b["name"] || "";

      var aa = chunkify(toChunkA.toString().toLowerCase());
      var bb = chunkify(toChunkB.toString().toLowerCase());

      for (let x = 0; aa[x] && bb[x]; x++) {
        if (aa[x] !== bb[x]) {
          var c = Number(aa[x]),
            d = Number(bb[x]);
          if (c === aa[x] && d === bb[x]) {
            return c - d;
          } else return aa[x] > bb[x] ? 1 : -1;
        }
      }
      return aa.length - bb.length;
    });

    // Prepare data
    const data = sortedReferees.map((referee) => {
      // Create base data object with required fields
      let refereeData = {
        name: referee.name || "",
      };

      // Only add country if enabled
      if (((tournament.defaultFields || {}).refereeCountry || {}).show) {
        refereeData.refereeCountry = referee.country || "";
      }

      // Only add share URL if enabled
      if (((tournament.defaultFields || {}).refereeShareURL || {}).show  ||
          ((tournament.defaultFields || {})["refereeShareURL"] || {}).show === undefined) {
        const loginData = loginCodes.find((l) => l.refereeId === referee.id);
        refereeData.refereeShareURL = loginData ? loginData.loginURL : "";
      }

      // Add any custom info fields
      Object.keys(tournament.infoFieldsReferees || {}).forEach((key) => {
        refereeData[key] = referee[key] || "";
      });

      return refereeData;
    });

    // Create Excel component
    const ExcelComponent = () => (
      <ExcelFile
        element={
          <button style={{ display: "none" }} id="downloadExcelButton">
            Download
          </button>
        }
        filename={
          tournament.useJudges
            ? languages.referees.judges
            : languages.schedule.referees + " - " + tournament.name
        }
      >
        <ExcelSheet
          data={data}
          name={
            tournament.useJudges
              ? languages.referees.judges
              : languages.schedule.referees
          }
        >
          {columns.map((column) => (
            <ExcelColumn
              key={column.id}
              label={column.label}
              value={(col) => col[column.id] || ""}
            />
          ))}
        </ExcelSheet>
      </ExcelFile>
    );

    // Create a temporary div and render the Excel component
    const tempDiv = document.createElement("div");
    document.body.appendChild(tempDiv);

    // Render the Excel component
    ReactDOM.render(<ExcelComponent />, tempDiv);

    // Trigger the download by clicking the hidden button
    setTimeout(() => {
      const downloadButton = document.getElementById("downloadExcelButton");
      if (downloadButton) {
        downloadButton.click();
      }

      // Cleanup
      setTimeout(() => {
        ReactDOM.unmountComponentAtNode(tempDiv);
        document.body.removeChild(tempDiv);
      }, 100);
    }, 0);
  } catch (error) {
    console.error("Error exporting referees:", error);
  }
};

// Wrapper component for backward compatibility
const ExportRefereesContainer = ({ tournament, teams, autoExport }) => {
  React.useEffect(() => {
    if (autoExport) {
      exportReferees(tournament, teams);
    }
  }, [autoExport, tournament, teams]);

  return null;
};

export default ExportRefereesContainer;
